import React, { FC, memo, ReactNode, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import BoxInfoImage from 'shared/assets/images/BoxInfoImage.png';
import {
  Box,
  parseDiscountData,
  useGetBoxDiscountedPriceString,
  useGetBoxRentalPriceString,
  useGetBoxRentalPriceStringWithVat,
} from 'entities/Box';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { InvoiceFrequencyType } from 'entities/Invoice';
import { getGlobalSettings } from 'app/appState';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { WarehouseDiscount, WarehouseForBooking } from 'entities/Warehouse';
import { Popup } from 'shared/ui/Popup';
import { ReactComponent as InfoIcon } from 'shared/assets/icons/InfoIcon.svg';
import { ReactComponent as CloseIcon } from 'shared/assets/icons/CloseIcon.svg';
import { FIRST_MONTH_DISCOUNT_REGEX } from 'shared/utils/regex';
import { UserAccountType } from 'entities/User';
import { getSizeCodeTypeString } from 'entities/SizeCode';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { UnitType } from 'entities/Contract';

export interface BookingMapBoxPreviewCardProps {
  box: Box;
  warehouseInfo: WarehouseForBooking;
  isShowPromotionPriceNeeded: boolean;
  accountType?: Nullable<UserAccountType>;
  onClose?: () => void;
}

interface CompactRowProps {
  label: string;
  value: ReactNode;
}

const CompactRow: FC<CompactRowProps> = memo(({ label, value }) => (
  <div className="grid grid-cols-[minmax(0,auto)_1fr] gap-x-3 gap-y-0.5 leading-snug">
    <span className="text-primaryLight shrink-0">{label}</span>
    <span className="text-right font-medium text-primary">{value}</span>
  </div>
));

CompactRow.displayName = 'CompactRow';

interface FullWidthRowProps {
  label: string;
  value: ReactNode;
  valueClassName?: string;
  className?: string;
}

const FullWidthRow: FC<FullWidthRowProps> = memo(({ label, value, className, valueClassName }) => (
  <div className={`flex w-full items-start justify-between gap-4 pt-3 first:border-t-0 first:pt-0 ${className ?? ''}`}>
    <span className="shrink-0 text-primaryLight">{label}</span>
    <span className={`min-w-0 flex-1 text-right font-medium text-primary ${valueClassName ?? ''}`}>{value}</span>
  </div>
));

FullWidthRow.displayName = 'FullWidthRow';

export const BookingMapBoxPreviewCard: FC<BookingMapBoxPreviewCardProps> = memo((props) => {
  const { box, warehouseInfo, isShowPromotionPriceNeeded, accountType, onClose } = props;
  const { t } = useAppTranslation('booking');

  const globalSettings = useAppSelector(getGlobalSettings);
  const showPriceWithVat = globalSettings?.showPriceWithVat;

  const discountedPrice = useGetBoxDiscountedPriceString({
    warehouseInfo,
    boxInfo: box,
    showPriceWithVat,
    accountType,
    unitType: box.unitType,
  });

  const firstMonthDiscount = useMemo((): WarehouseDiscount | undefined => {
    if (!warehouseInfo?.warehouseDiscounts) {
      return;
    }

    const sortedDiscounts = [...warehouseInfo.warehouseDiscounts].sort((a, b) => a.priority - b.priority);

    return sortedDiscounts?.find((discount) => {
      const includesFirstMonth = discount.discounts.split(',').some((d) => FIRST_MONTH_DISCOUNT_REGEX.test(d.trim()));
      const hasUnitSizes = Boolean(discount.unitSizes?.length);

      if (hasUnitSizes) {
        const unitSizes = discount.unitSizes.split(',').map((size) => Number(size));
        const includesSize = unitSizes.includes(box.sizeCode.square);
        return includesFirstMonth && includesSize;
      }

      return includesFirstMonth && discount.unitType === box.unitType;
    });
  }, [box, warehouseInfo?.warehouseDiscounts]);

  const parsedDiscount = parseDiscountData(firstMonthDiscount);

  const boxMonthRentalPriceWithVat = useGetBoxRentalPriceStringWithVat(box, InvoiceFrequencyType.MONTH, warehouseInfo, accountType);
  const boxMonthRentalPrice = useGetBoxRentalPriceString(box);
  const rentalPriceMonth = showPriceWithVat ? boxMonthRentalPriceWithVat : boxMonthRentalPrice;

  const boxImage = box.imageUrl || box.sizeCode.imageUrl || BoxInfoImage;

  const size = `${roundNumber(box.height)}m (${t('H')}) × ${roundNumber(box.width)}m (${t('W')}) × ${roundNumber(box.length)}m (${t('D')})`;
  const square = `${box.sizeCode.square} ${getSizeCodeTypeString(box.sizeCode.sizeCodeType)}`;
  const volume = `${roundNumber(box.height * box.width * box.length)} m³`;

  const unitNumberLabel = t(`${box.unitType} number`);
  const regularMonthLabel = t('Regular price');

  const promoBlock =
    discountedPrice && isShowPromotionPriceNeeded ? (
      <FullWidthRow
        label={t('Promotional price')}
        className="mt-3 border-t"
        value={
          <span className="inline-flex items-start justify-end gap-1.5">
            <span className="min-w-0 break-words">{discountedPrice}</span>
            <Popup description={<div className="max-w-[260px]">{parsedDiscount?.description}</div>}>
              <button
                type="button"
                className="pointer-events-auto mt-0.5 shrink-0 cursor-pointer border-0 bg-transparent p-0"
                aria-label={t('Details', { defaultValue: 'Details' })}
              >
                <InfoIcon width={16} height={16} className="[&>path]:stroke-primary" />
              </button>
            </Popup>
          </span>
        }
      />
    ) : null;

  return (
    <div className="relative box-border flex w-full min-w-0 flex-col gap-3 overflow-hidden rounded-lg border border-secondaryAccent bg-white p-4 shadow-md sm:p-5">
      {onClose ? (
        <button
          type="button"
          aria-label={t('Close')}
          className="pointer-events-auto absolute right-0 top-0 z-10 flex h-8 w-8 items-center justify-center rounded-none border-0 bg-transparent p-0 hover:bg-secondaryAccent"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <CloseIcon width={18} height={18} className="[&>path]:stroke-primaryLight" />
        </button>
      ) : null}
      <div className="flex gap-4">
        <div className="shrink-0">
          <img src={boxImage} alt="" className="h-[88px] w-[88px] rounded-md object-cover" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5 pr-6">
          <CompactRow label={unitNumberLabel} value={box.name} />
          <CompactRow label={t('Square')} value={square} />
          {box.unitType === UnitType.BOX ? <CompactRow label={t('Volume')} value={volume} /> : null}
          {box.subtype && (
            <Popup
              description={<div className="max-w-[310px] desktop:max-w-[600px]">{getLocalizedString(box?.specialityDescription)}</div>}
              isDisabled={!getLocalizedString(box?.specialityDescription)}
            >
              <div className="flex items-center space-x-1.5 w-fit pointer-events-auto">
                <InfoIcon width={20} height={20} className="[&>path]:stroke-accent cursor-pointer flex-shrink-0" />
                <span className="text-accent font-semibold">{t(box.subtype)}</span>
              </div>
            </Popup>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-0">
        {box.unitType === UnitType.BOX ? <FullWidthRow label={t('Box size')} value={size} valueClassName="break-words" /> : null}
        <FullWidthRow label={regularMonthLabel} value={rentalPriceMonth} />
        {promoBlock}
      </div>
    </div>
  );
});

BookingMapBoxPreviewCard.displayName = 'BookingMapBoxPreviewCard';
